/**
 * canvasWritePoint — LE POINT D'ÉCRITURE UNIQUE de la génération en flux (#937).
 *
 * > Un seul objet porte la file, les deux curseurs, la borne d'attente et la
 * > condamnation. Rien d'autre dans le canvas ne décide « ai-je déjà écrit ce
 * > bloc ? » — c'est la file qui le sait, et elle seule.
 *
 * ## Pourquoi une file, alors que l'insertion est synchrone en 1.44.1
 *
 * L'ordre des paragraphes d'un acte est aujourd'hui garanti *structurellement*
 * par la synchronicité de `insertContentAt` : le delta N+1 ne peut pas partir
 * avant que le delta N ne soit posé, puisque rien ne rend la main entre les
 * deux. La façade navigateur de SuperDoc V2 enveloppe toute opération dans un
 * `MaybePromise` : cette garantie doit donc devenir **écrite** avant que la
 * version ne change, pas après. Chaque delta s'enchaîne sur la **résolution**
 * du précédent (`#file.then(...)`), et l'exécuteur d'écriture peut rendre une
 * promesse comme une valeur — la file ne s'appuie jamais sur sa synchronicité
 * (c'est ce que #939 rendra effectivement asynchrone).
 *
 * ## Deux curseurs, et c'est le cœur de la correction
 *
 * Un curseur unique « qui n'avance qu'après succès » **duplique** : l'effet
 * React qui pousse les blocs se redéclenche à chaque bloc reçu, et rejouerait
 * tout ce qui n'est pas encore confirmé — le premier bloc écrit deux fois, le
 * deuxième trois fois, et un acte long sort avec un préfixe répété. D'où :
 *  - `curseurEmis` avance **à la mise en file** — c'est la garde de
 *    ré-entrance, elle empêche le même bloc d'être mis en file deux fois ;
 *  - `curseurConfirme` avance **au succès uniquement** — c'est lui, et lui
 *    seul, qui arme le contrôleur d'enregistrement du scope côté hôte.
 *
 * ## La borne est fail-closed — et elle a fini par se déclencher POUR DE VRAI
 *
 * ⚠️ **Ce docblock a longtemps dit que ce mode d'échec n'avait jamais été
 * observé.** C'était vrai sous `superdoc@1.44.1`, où l'insertion synchrone ne
 * pouvait pas pendre. Ça ne l'est plus : le 2026-08-26, un `write_markdown`
 * `mode:"replace"` de **166 blocs** (une assignation complète, 39 343
 * caractères) a franchi la borne sur un document qui en contenait déjà 104
 * (conversation CD3 `a7c3adc1`). Ce n'était pas une promesse morte — c'était
 * une écriture simplement LONGUE.
 *
 * L'hypothèse fondatrice (« une écriture normale se compte en millisecondes,
 * dépasser la borne signifie qu'une promesse ne se résoudra jamais ») ne tient
 * donc que pour de PETITS lots. D'où le découpage en tranches ci-dessous : la
 * borne garde désormais une tranche, pas un acte entier. Elle redevient ce
 * qu'elle prétend être — un détecteur de promesse morte — au lieu d'être un
 * plafond de volume déguisé.
 *
 * ## Le découpage en tranches (2026-08-26)
 *
 * Un delta plus long que `TAILLE_DE_TRANCHE_BLOCS` est posé en plusieurs
 * écritures successives, **chacune bornée séparément**. Deux gains, et le
 * second est le plus important :
 *
 *  1. un acte de vingt à quarante pages passe, parce qu'aucune écriture
 *     unitaire n'a à tenir dans la borne ;
 *  2. quand une tranche saute, on sait **exactement combien de blocs sont
 *     posés**. Avant, un dépassement laissait le document dans un état compris
 *     entre 0 et N blocs écrits, et le receipt affirmait « rien n'a été écrit »
 *     faute de mieux — un mensonge qui a fait dire à l'agent, le 2026-08-26,
 *     « rien n'a été écrasé ni dupliqué » alors que l'acte était en double à
 *     l'écran.
 *
 * ⚠️ La tranche COUPÉE par la borne reste indéterminée : sa promesse n'est pas
 * annulable (#932), elle continue de poser ses blocs en arrière-plan. C'est
 * pourquoi `partielle` distingue les blocs **confirmés** des blocs
 * **indéterminés** — les confondre rendrait un chiffre faux avec un air de
 * précision.
 *
 * Ce qui est load-bearing, et seulement ça : **refuser toute écriture
 * postérieure après un abandon**. Une borne qui laisserait simplement passer le
 * delta suivant détruirait l'invariant qu'elle sert — l'écriture abandonnée, si
 * elle aboutit malgré tout, muterait le document *après* qu'un delta postérieur
 * a commencé, et l'acte sortirait dans le désordre. #932 a tranché : les `.d.ts`
 * publics du Document API ne déclarent ni signal d'abandon, ni annulation, ni
 * borne native, et aucun retour n'est un handle annulable — on ne peut donc pas
 * annuler l'opération. La seule branche restante est de **condamner la file**.
 *
 * Au dépassement : le fragment déjà écrit **reste** (rien ne le supprime, il est
 * dans l'éditeur et l'auto-enregistrement l'a persisté), une erreur est
 * **affichée** à l'avocat (`surCondamnation`) — jamais un blocage muet — et
 * aucune écriture n'est plus acceptée avant remontage de session. **Aucune
 * reprise automatique** n'est introduite ici : la reprise emprunte les chemins
 * existants (l'avocat relance, ou l'agent relance son appel).
 *
 * **Un échec d'écriture est traité exactement pareil** (`#surEchec`). Le curseur
 * d'émission a déjà avancé, donc laisser passer le delta suivant produirait un
 * acte avec un **trou** au milieu, sans erreur affichée, persisté comme complet
 * par l'auto-enregistrement de fin. Un échec ponctuel suivi du delta suivant
 * *est* une reprise après abandon.
 *
 * ## Époque de montage et session documentaire
 *
 * La file est créée **par montage** du canvas et `abandonner()` est appelé au
 * démontage. Une écriture qui se résout après l'abandon ne touche donc ni les
 * curseurs ni l'enregistrement : un insert expiré puis résolu tardivement ne
 * peut ni se réordonner devant une écriture postérieure (la file est condamnée)
 * ni traverser un changement de session (la file est abandonnée).
 */

import type { StructuredBlock } from '@hector/shared-types';

/** Une écriture peut rendre une promesse (V2) ou une valeur (1.44.1). */
export type PeutEtreUnePromesse<T> = T | Promise<T>;

/**
 * Borne d'attente d'**UNE TRANCHE**, en millisecondes.
 *
 * Elle n'existe pas pour arbitrer une lenteur mais pour garantir que l'écran ne
 * se fige jamais en silence. Depuis le découpage (2026-08-26) elle ne borne
 * plus un acte entier mais une tranche d'au plus `TAILLE_DE_TRANCHE_BLOCS`
 * blocs — ce qui lui rend son sens : à cette échelle, la dépasser signifie bien
 * qu'une promesse ne se résoudra pas.
 *
 * Relevée de 15 s à 20 s au découpage, puis à 60 s le 2026-10-09 sur mesure de
 * production (PostHog `canvas_write_diagnostic`, 2026-10-04 → 10-08, ~1 400
 * tranches de 20 blocs) : une tranche pleine s'y pose en 7 s en médiane, 14 s au
 * 90e centile, et 1 sur 100 touchait les 20 s — soit 3 à 8 % des tranches
 * pleines condamnées pour une simple lenteur, à document intact. L'hypothèse
 * « une tranche coûte moins du quart de la borne » (ci-dessous) ne tient que
 * sur la machine de référence ; les postes des cabinets sont ~3× plus lents.
 *
 * ⚠️ La vraie queue de distribution reste inconnue : à 20 s la mesure était
 * censurée par la borne elle-même. 60 s est donc un relevé prudent (≈ 8× la
 * médiane), à recaler sur `chunk_completed.duration_ms` une fois en production.
 * Rester sous `BORNE_POSE_DE_DOCUMENT_MS` (120 s, backend
 * `SuperDocCanvasTools.ts`), qui borne l'opération ENTIÈRE d'un verbe d'agent.
 */
export const BORNE_ECRITURE_MS = 60_000;

/**
 * Nombre maximal de blocs posés en UNE écriture bornée.
 *
 * Source : `tests/ws-mocked/mesure-cout-pose-blocs.spec.ts` (projet `mesures`),
 * relevé du 2026-08-27, `test-results/cout-pose-blocs.json`.
 *
 * ⚠️ **Le nombre de blocs est une MAUVAISE unité de coût, et c'est le résultat
 * central du relevé.** À nombre égal, le coût varie d'un facteur 4 selon la
 * composition : 160 blocs de prose nue se posent en 3 653 ms, 160 blocs d'acte
 * réaliste (titres + listes + tables) en **15 726 ms**. C'est ce facteur, et
 * lui seul, qui explique l'incident du 2026-08-26 — 166 blocs d'assignation
 * franchissant une borne de 15 s : ni machine lente, ni document de départ
 * volumineux ne sont nécessaires pour le reproduire. Le « point de bascule »
 * cherché entre 78 et 166 blocs n'existe donc pas comme nombre : il vaut ~600
 * blocs en prose nue et ~155 en acte réaliste.
 *
 * ⚠️ **Le coût par bloc CROÎT avec la taille déjà atteinte du document** —
 * découper ne supprime pas ce terme, il le borne. Mesuré à +30 % sur 80 blocs
 * de prose entre un document vide et un document de 244 blocs ; ×1,8 sur
 * matériau d'acte en queue d'un document de 365 blocs. Une tranche doit donc
 * être dimensionnée sur la DERNIÈRE d'un acte long, jamais sur la première.
 *
 * D'où 20 plutôt que 40. L'invariant retenu : une tranche coûte moins du quart
 * de la borne en queue d'un acte de 40 pages, sur la machine de référence. À
 * 40, la pire tranche mesurée coûte 7 281 ms, soit 2,75× de marge seulement —
 * un poste trois fois plus lent franchirait la borne. À 20, la marge passe à
 * 5,5×. Le prix est de +6,7 % sur un acte de 320 blocs (frontières de tranche
 * supplémentaires, majorées à 273 ms chacune) : c'est le coût d'un facteur 2 de
 * sécurité.
 */
export const TAILLE_DE_TRANCHE_BLOCS = 20;

/** Découpe un delta en tranches d'au plus `taille` blocs, dans l'ordre. */
function decouperEnTranches(
  delta: readonly StructuredBlock[],
  taille: number,
): readonly (readonly StructuredBlock[])[] {
  if (taille <= 0 || delta.length <= taille) return [delta];
  const tranches: (readonly StructuredBlock[])[] = [];
  for (let debut = 0; debut < delta.length; debut += taille) {
    tranches.push(delta.slice(debut, debut + taille));
  }
  return tranches;
}

export type ResultatEcriture =
  | { statut: 'ecrit'; blocsEcrits: number; curseurConfirme: number }
  /** Rien de neuf à écrire — le delta est vide. */
  | { statut: 'rien' }
  /**
   * `flux-en-cours` n'est PAS produit par la file : c'est la politique
   * d'écriture qui refuse un acte rendu tant qu'un flux vit
   * (`ecrireUnActeGenere`, `canvasWritePolicy.ts`). Il vit dans cette union
   * parce que l'appelant traite un seul type de résultat, et qu'un refus rendu
   * sous une autre forme lui échapperait.
   */
  | { statut: 'refusee'; raison: 'condamnee' | 'abandonnee' | 'travail-remplace' | 'flux-en-cours' }
  /**
   * L'écriture a jeté. La file est **condamnée** du même geste — voir
   * `#condamner` : un échec ponctuel suivi du delta suivant serait une reprise
   * après abandon, avec un trou persisté au milieu de l'acte.
   */
  | { statut: 'echec'; erreur: Error }
  /**
   * Une ou plusieurs tranches sont posées, puis la borne a coupé (2026-08-26).
   *
   * ⚠️ **Ce statut existe parce que son absence a produit un mensonge.** Avant
   * lui, un dépassement rendait `{statut:'refusee', raison:'condamnee'}` —
   * exactement la même valeur qu'un refus AVANT toute écriture. L'appelant ne
   * pouvait pas les distinguer, alors il supposait le cas sûr et annonçait
   * « refusé avant toute modification, le document est intact ». Le 2026-08-26,
   * l'agent a relayé cette phrase à l'avocat pendant que l'assignation
   * s'affichait EN DOUBLE devant lui.
   *
   * Les trois compteurs ne sont pas redondants, ils disent trois choses :
   *  - `blocsConfirmes` — tranches abouties. Ces blocs SONT dans le document.
   *  - `blocsIndetermines` — la tranche coupée par la borne. Sa promesse n'est
   *    pas annulable (#932) et continue de poser en arrière-plan : ces blocs
   *    sont posés en tout, en partie, ou pas du tout. **Nul ne le sait sans
   *    relire le document.**
   *  - `blocsNonEcrits` — jamais mis en écriture. Ceux-là sont sûrs.
   */
  | {
      statut: 'partielle';
      blocsConfirmes: number;
      blocsIndetermines: number;
      blocsNonEcrits: number;
      curseurConfirme: number;
    };

export interface OptionsPointDEcriture {
  /**
   * Pose le delta dans le document : conversion HTML, insertion en fin de
   * document et normalisation structurelle. Peut rendre une promesse.
   */
  ecrire: (delta: readonly StructuredBlock[]) => PeutEtreUnePromesse<void>;
  /** Borne d'attente fail-closed, par TRANCHE. Injectable pour les tests. */
  borneMs?: number;
  /** Taille de tranche en blocs. Injectable pour les tests. */
  tailleDeTranche?: number;
  /**
   * Prévient l'hôte que la file est condamnée, pour que l'avocat le voie.
   * Appelé une seule fois par file.
   */
  surCondamnation?: (message: string) => void;
  /** Identifiant stable entre la tentative initiale et ses reprises. */
  creerIdOperation?: () => string;
  /** Observation sans contenu des reprises transitoires (#1405). */
  surDiagnostic?: (diagnostic: DiagnosticPointDEcriture) => void;
}

/** Corrélation technique optionnelle apportée par une requête Canvas agent. */
export interface CanvasWriteRequestMetadata {
  readonly requestId?: string;
  /**
   * Content-free observation for a correlated Canvas operation. Called only
   * after a chunk was laid successfully; it cannot affect the writer outcome.
   */
  readonly onChunkCompleted?: (progress: { completedUnits: number; totalUnits: number }) => void;
}

interface DiagnosticTranchePointDEcriture {
  readonly operationId: string;
  readonly requestId?: string;
  readonly writeGeneration: number;
  readonly chunkIndex: number;
  readonly chunkCount: number;
  readonly chunkBlockCount: number;
  readonly attemptIndex: number;
  readonly cursorEmitted: number;
  readonly cursorConfirmed: number;
}

/** Observation de durée sans delta Markdown, HTML ni identifiant de bloc SuperDoc. */
export type DiagnosticPointDEcriture =
  | (DiagnosticTranchePointDEcriture & {
      readonly stage: 'chunk_started';
    })
  | (DiagnosticTranchePointDEcriture & {
      readonly stage: 'chunk_completed' | 'chunk_timed_out';
      readonly durationMs: number;
    })
  | (DiagnosticTranchePointDEcriture & {
      readonly stage: 'transient_retry';
      readonly providerReason: string;
      readonly retryDelayMs: number;
    });

/** Sentinelle interne du dépassement de borne — jamais exposée à l'appelant. */
const DEPASSEMENT = Symbol('borne-ecriture-depassee');

/**
 * Refus d'écriture dû à un état **TRANSITOIRE** du fournisseur, pas à une
 * permission.
 *
 * ⚠️ **Le message du fournisseur ment, et il nous a coûté deux jours**
 * (mesuré le 2026-08-27). Le moteur docx rend
 * `PERMISSION_DENIED: insert is unavailable while the document is in read-only
 * review mode.` — ce qui accuse un mode de révision. Or l'expérience contrôlée
 * dit l'inverse : `documentMode` vaut `editing` d'un bout à l'autre,
 * `setDocumentMode` n'est JAMAIS appelé, et le même insert rejoué une fois la
 * sauvegarde terminée passe. Le seul discriminant honnête est
 * `failure.details.reason`, qui vaut `review-surface-read-only` : la surface du
 * moteur quitte son état inscriptible **le temps d'un export**, et toute
 * écriture qui tombe dans cette fenêtre est refusée.
 *
 * Traiter ce refus comme une panne condamnait la file, donc arrêtait la
 * rédaction au milieu de l'acte — et en `mode:"replace"` la suppression de
 * l'ancien contenu ne tournait jamais, d'où l'acte affiché EN DOUBLE. C'est le
 * défaut que l'avocat a subi deux fois cette semaine.
 */
export class RefusTransitoireDuProvider extends Error {
  readonly raison: string;

  constructor(message: string, raison: string) {
    super(message);
    this.name = 'RefusTransitoireDuProvider';
    this.raison = raison;
  }
}

/**
 * Délais avant chaque reprise, en millisecondes.
 *
 * La fenêtre de sauvegarde se compte en dizaines à centaines de millisecondes
 * (13,8 ms mesurées sur un document de 40 blocs, davantage sur un acte long où
 * la sérialisation docx est plus lourde). Trois reprises couvrent donc
 * largement le cas, pour 1,55 s au pire — négligeable devant la borne de
 * tranche, et devant les 120 s du courtier backend.
 */
const REPRISES_MS: readonly number[] = [150, 400, 1_000];

export class PointDEcritureCanvas {
  readonly #ecrire: OptionsPointDEcriture['ecrire'];
  readonly #borneMs: number;
  readonly #tailleDeTranche: number;
  readonly #surCondamnation?: (message: string) => void;
  readonly #creerIdOperation: () => string;
  readonly #surDiagnostic?: (diagnostic: DiagnosticPointDEcriture) => void;

  /** Avance à la MISE EN FILE — garde de ré-entrance. */
  #curseurEmis = 0;
  /** Avance au SUCCÈS — seul à armer l'enregistrement. */
  #curseurConfirme = 0;
  /** Queue de sérialisation : chaque delta s'enchaîne sur la résolution du précédent. */
  #file: Promise<unknown> = Promise.resolve();
  #condamnee = false;
  #abandonnee = false;
  /**
   * Maillons mis en file et pas encore résolus. Lu par l'autosave du scope
   * (#1404) : un export pendant une pose fait refuser l'écriture par SuperDoc.
   */
  #maillonsEnVol = 0;
  /**
   * ÉPOQUE DE TRAVAIL — incrémentée par `reinitialiser()`.
   *
   * Capturée à la mise en file, re-contrôlée à la résolution du maillon. Sans
   * elle, la séquence « delta du job A en vol → job B prend sa place →
   * `reinitialiser()` » laisserait la continuation de A écrire dans le document
   * de B, poser le curseur confirmé à la cible de A (par ex. 12) alors que B en
   * est à 1, et rendre `{statut:'ecrit'}` — donc **armer l'enregistrement au nom
   * d'un travail abandonné**. Trois mutations silencieuses pour un seul oubli.
   */
  #generation = 0;
  /**
   * Attentes bornées en cours. `abandonner()` les clôt : aucun `setTimeout` ne
   * doit survivre au démontage du canvas, sinon la borne condamne dans le vide
   * 15 s plus tard et prévient un hôte qui n'existe plus.
   */
  readonly #attentesBornees = new Set<() => void>();

  constructor(options: OptionsPointDEcriture) {
    this.#ecrire = options.ecrire;
    this.#borneMs = options.borneMs ?? BORNE_ECRITURE_MS;
    this.#tailleDeTranche = options.tailleDeTranche ?? TAILLE_DE_TRANCHE_BLOCS;
    this.#surCondamnation = options.surCondamnation;
    this.#creerIdOperation = options.creerIdOperation ?? (() => {
      if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
      return `canvas-write-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    });
    this.#surDiagnostic = options.surDiagnostic;
  }

  get curseurEmis(): number {
    return this.#curseurEmis;
  }

  get curseurConfirme(): number {
    return this.#curseurConfirme;
  }

  get estCondamnee(): boolean {
    return this.#condamnee;
  }

  get estAbandonnee(): boolean {
    return this.#abandonnee;
  }

  /** Vrai tant qu'au moins un delta est en file ou en cours de pose. */
  get ecritureEnVol(): boolean {
    return this.#maillonsEnVol > 0;
  }

  /**
   * Met en file le delta de `blocs` non encore émis.
   *
   * L'appelant passe **tout** le tableau de blocs du flux : c'est la file qui
   * calcule le delta depuis son propre curseur d'émission. Lui laisser calculer
   * le delta rouvrirait la porte à la duplication que les deux curseurs
   * ferment.
   */
  pousser(
    blocs: readonly StructuredBlock[],
    metadata: CanvasWriteRequestMetadata = {},
  ): Promise<ResultatEcriture> {
    if (this.#abandonnee) return Promise.resolve({ statut: 'refusee', raison: 'abandonnee' });
    if (this.#condamnee) return Promise.resolve({ statut: 'refusee', raison: 'condamnee' });
    if (blocs.length <= this.#curseurEmis) return Promise.resolve({ statut: 'rien' });

    const delta = blocs.slice(this.#curseurEmis);
    const cible = blocs.length;
    // AVANT le moindre await : c'est ce qui rend l'effet appelant ré-entrant
    // sans duplication.
    this.#curseurEmis = cible;
    const generation = this.#generation;
    const operationId = this.#creerIdOperation();

    this.#maillonsEnVol += 1;
    const tache = this.#file.then(() => this.#executer(
      delta,
      cible,
      generation,
      operationId,
      metadata.requestId,
      metadata.onChunkCompleted,
    ));
    // La file ne doit jamais se rompre : un maillon en échec laisserait tous
    // les suivants pendus. On la ré-ancre sur une promesse toujours tenue.
    this.#file = tache.then(
      () => { this.#maillonsEnVol -= 1; },
      () => { this.#maillonsEnVol -= 1; },
    );
    return tache;
  }

  /**
   * Aligne les deux curseurs sans écrire — les blocs sont **consommés**.
   *
   * Sert la garde d'époque de job : un job périmé ne doit pas s'appender dans un
   * document que l'avocat a ouvert entre-temps, mais ses blocs ne doivent pas
   * non plus rester en attente d'écriture pour toujours.
   */
  consommerSansEcrire(nombreDeBlocs: number): void {
    if (nombreDeBlocs <= this.#curseurEmis) return;
    this.#curseurEmis = nombreDeBlocs;
    this.#curseurConfirme = nombreDeBlocs;
  }

  /**
   * Nouveau travail : le tableau de blocs repart de zéro, les curseurs aussi, et
   * **l'époque de travail avance** — ce qui invalide les écritures en vol du
   * travail précédent (cf. `#generation`).
   */
  reinitialiser(): void {
    this.#curseurEmis = 0;
    this.#curseurConfirme = 0;
    this.#generation++;
  }

  /**
   * Abandon définitif — appelé au démontage du canvas. Toute écriture en vol qui
   * se résoudrait ensuite ne touche plus rien, et **aucune borne ne survit** :
   * les attentes en cours sont clôturées immédiatement plutôt que laissées
   * courir jusqu'à leur échéance.
   */
  abandonner(): void {
    this.#abandonnee = true;
    for (const clore of [...this.#attentesBornees]) clore();
    this.#attentesBornees.clear();
  }

  /**
   * Se résout quand plus aucune écriture n'est en vol.
   *
   * ⚠️ Le cas critique que cette méthode existe pour tenir : le **dernier bloc
   * reçu dans le même cycle que la fin du flux**. L'enregistrement de fin
   * exporterait alors le document *avant* que l'écriture différée n'ait abouti,
   * et livrerait un acte tronqué. La boucle re-teste la file après chaque
   * attente : une écriture mise en file *pendant* qu'on attend doit elle aussi
   * être attendue.
   */
  async auRepos(): Promise<void> {
    let observee: Promise<unknown> | null = null;
    while (this.#file !== observee) {
      observee = this.#file;
      await observee.then(
        () => undefined,
        () => undefined,
      );
    }
  }

  async #executer(
    delta: readonly StructuredBlock[],
    cible: number,
    generation: number,
    operationId: string,
    requestId: string | undefined,
    onChunkCompleted: CanvasWriteRequestMetadata['onChunkCompleted'],
  ): Promise<ResultatEcriture> {
    const barrage = this.#barrage(generation);
    if (barrage) return barrage;

    const tranches = decouperEnTranches(delta, this.#tailleDeTranche);
    let confirmes = 0;

    for (const [chunkIndex, tranche] of tranches.entries()) {
      // Re-contrôler ENTRE deux tranches, pas seulement à l'entrée : un
      // démontage ou un changement de travail pendant une écriture longue ne
      // doit pas voir les tranches suivantes partir dans un document qui n'est
      // plus le bon. Ce qui est déjà posé l'est ; on cesse d'en ajouter.
      if (confirmes > 0) {
        const barrageEntreTranches = this.#barrage(generation);
        if (barrageEntreTranches) return barrageEntreTranches;
      }

      const pose = await this.#poserLaTranche(tranche, {
        operationId,
        ...(requestId === undefined ? {} : { requestId }),
        writeGeneration: generation,
        chunkIndex,
        chunkCount: tranches.length,
      });
      if (pose.issue === 'depassement') {
        return this.#surDepassement(confirmes, tranche.length, delta.length, cible);
      }
      if (pose.issue === 'echec') return this.#surEchec(confirmes, delta.length, pose.erreur);
      // This is the first point at which a unit is factual: the provider call
      // resolved inside the bounded writer. A diagnostic or queued chunk is not
      // enough to report document progress to the backend.
      try {
        onChunkCompleted?.({ completedUnits: confirmes + tranche.length, totalUnits: delta.length });
      } catch {
        // Observability must not poison the single authoritative write queue.
      }
      confirmes += tranche.length;
    }

    // Relire APRÈS l'attente : la session ou le travail ont pu changer pendant
    // l'écriture.
    const barrageTardif = this.#barrage(generation);
    if (barrageTardif) return barrageTardif;

    this.#curseurConfirme = cible;
    return { statut: 'ecrit', blocsEcrits: delta.length, curseurConfirme: cible };
  }

  /**
   * Pose UNE tranche, en reprenant sur un refus transitoire du fournisseur.
   *
   * ⚠️ **La reprise n'est pas une politesse, c'est la correction du défaut du
   * 2026-08-27.** Le moteur docx refuse toute écriture pendant qu'un export est
   * en vol — et l'auto-sauvegarde en déclenche un toutes les 2,5 s après une
   * frappe. Sans reprise, la collision condamnait la file : rédaction arrêtée
   * au milieu de l'acte, et en `mode:"replace"` l'ancien contenu jamais
   * supprimé, donc l'acte affiché en double.
   *
   * La reprise est **étroite à dessein** : uniquement sur
   * `RefusTransitoireDuProvider`, que l'écrivain ne construit que pour un refus
   * passager rendu sous un mode inscriptible (`refusPassagerSousCeMode`,
   * `state/canvas-agent/refusPassager.ts`). Un refus qui est une VRAIE
   * permission, ou une lecture seule durable (`viewing`), n'est pas repris — le
   * rejouer en boucle serait pire que de l'annoncer.
   */
  async #poserLaTranche(
    tranche: readonly StructuredBlock[],
    contexte: Pick<DiagnosticTranchePointDEcriture, 'operationId' | 'requestId' | 'writeGeneration' | 'chunkIndex' | 'chunkCount'>,
  ): Promise<{ issue: 'posee' } | { issue: 'depassement' } | { issue: 'echec'; erreur: unknown }> {
    for (let tentative = 0; ; tentative += 1) {
      const commenceA = Date.now();
      this.#emettreDiagnostic({
        stage: 'chunk_started',
        ...contexte,
        chunkBlockCount: tranche.length,
        attemptIndex: tentative + 1,
        cursorEmitted: this.#curseurEmis,
        cursorConfirmed: this.#curseurConfirme,
      });
      let ecriture: Promise<void>;
      try {
        ecriture = Promise.resolve(this.#ecrire(tranche));
      } catch (erreur) {
        if (!this.#peutReprendre(erreur, tentative)) return { issue: 'echec', erreur };
        const delai = REPRISES_MS[tentative] ?? 0;
        this.#emettreReprise(contexte, tranche.length, tentative + 1, erreur, delai);
        await this.#attendre(delai);
        continue;
      }
      // Une écriture abandonnée n'a plus personne pour lire son sort : sans ce
      // catch, son rejet tardif remonterait en unhandledRejection.
      ecriture.catch(() => undefined);

      try {
        await this.#borner(ecriture);
        // `abandonner()` dénoue la borne sans que la promesse fournisseur ait
        // fini : ne jamais enregistrer ce réveil comme une écriture aboutie.
        if (!this.#abandonnee) {
          this.#emettreDiagnostic({
            stage: 'chunk_completed',
            ...contexte,
            chunkBlockCount: tranche.length,
            attemptIndex: tentative + 1,
            cursorEmitted: this.#curseurEmis,
            cursorConfirmed: this.#curseurConfirme,
            durationMs: Math.max(0, Date.now() - commenceA),
          });
        }
        return { issue: 'posee' };
      } catch (erreur) {
        if (erreur === DEPASSEMENT) {
          this.#emettreDiagnostic({
            stage: 'chunk_timed_out',
            ...contexte,
            chunkBlockCount: tranche.length,
            attemptIndex: tentative + 1,
            cursorEmitted: this.#curseurEmis,
            cursorConfirmed: this.#curseurConfirme,
            durationMs: Math.max(0, Date.now() - commenceA),
          });
          return { issue: 'depassement' };
        }
        if (!this.#peutReprendre(erreur, tentative)) return { issue: 'echec', erreur };
        const delai = REPRISES_MS[tentative] ?? 0;
        this.#emettreReprise(contexte, tranche.length, tentative + 1, erreur, delai);
        await this.#attendre(delai);
      }
    }
  }

  #emettreReprise(
    contexte: Pick<DiagnosticTranchePointDEcriture, 'operationId' | 'requestId' | 'writeGeneration' | 'chunkIndex' | 'chunkCount'>,
    chunkBlockCount: number,
    attemptIndex: number,
    erreur: unknown,
    retryDelayMs: number,
  ): void {
    if (!(erreur instanceof RefusTransitoireDuProvider)) return;
    this.#emettreDiagnostic({
      stage: 'transient_retry',
      ...contexte,
      chunkBlockCount,
      attemptIndex,
      cursorEmitted: this.#curseurEmis,
      cursorConfirmed: this.#curseurConfirme,
      providerReason: erreur.raison,
      retryDelayMs,
    });
  }

  #emettreDiagnostic(diagnostic: DiagnosticPointDEcriture): void {
    try {
      this.#surDiagnostic?.(diagnostic);
    } catch {
      // Une sonde ne doit jamais condamner la file qu'elle observe.
    }
  }

  /** Une reprise n'a de sens que sur un refus transitoire, et si la file vit encore. */
  #peutReprendre(erreur: unknown, tentative: number): boolean {
    if (tentative >= REPRISES_MS.length) return false;
    if (this.#abandonnee || this.#condamnee) return false;
    return erreur instanceof RefusTransitoireDuProvider;
  }

  /**
   * Attente de reprise, close par `abandonner()` comme les attentes bornées :
   * aucun minuteur ne doit survivre au démontage du canvas.
   */
  #attendre(delaiMs: number): Promise<void> {
    return new Promise<void>((resoudre) => {
      let clore = (): void => undefined;
      const echeance = setTimeout(() => {
        this.#attentesBornees.delete(clore);
        resoudre();
      }, delaiMs);
      clore = (): void => {
        clearTimeout(echeance);
        this.#attentesBornees.delete(clore);
        resoudre();
      };
      this.#attentesBornees.add(clore);
    });
  }

  /**
   * Une tranche a franchi la borne : la file est condamnée, et le résultat dit
   * EXACTEMENT ce qui est acquis.
   *
   * Le curseur confirmé avance jusqu'aux tranches abouties — et pas au-delà.
   * L'avancer jusqu'à la tranche coupée mentirait ; ne pas l'avancer du tout
   * ferait passer pour perdus des blocs qui sont bel et bien dans le document,
   * et l'hôte n'armerait pas l'enregistrement du texte que l'avocat voit.
   */
  #surDepassement(
    confirmes: number,
    indetermines: number,
    totalDuDelta: number,
    cible: number,
  ): ResultatEcriture {
    const nonEcrits = totalDuDelta - confirmes - indetermines;
    const curseurConfirme = cible - totalDuDelta + confirmes;
    this.#curseurConfirme = curseurConfirme;
    this.#condamner(
      `L'écriture dans le document n'a pas abouti : ${confirmes} bloc(s) posés, `
      + `${indetermines} en cours au moment de la coupure (borne de ${this.#borneMs} ms dépassée), `
      + `${nonEcrits} non écrits. Le texte déjà posé est conservé ; rouvrez le document `
      + 'pour relancer la rédaction sur ce qui manque.',
    );
    return { statut: 'partielle', blocsConfirmes: confirmes, blocsIndetermines: indetermines, blocsNonEcrits: nonEcrits, curseurConfirme };
  }

  /**
   * Ce qui interdit à un maillon de toucher quoi que ce soit — évalué à son
   * entrée ET après son attente. Rend `null` quand la voie est libre.
   */
  #barrage(generation: number): ResultatEcriture | null {
    if (this.#abandonnee) return { statut: 'refusee', raison: 'abandonnee' };
    if (this.#condamnee) return { statut: 'refusee', raison: 'condamnee' };
    if (generation !== this.#generation) return { statut: 'refusee', raison: 'travail-remplace' };
    return null;
  }

  /**
   * Un échec d'écriture CONDAMNE la file, comme le dépassement de borne.
   *
   * ⚠️ Ne pas « laisser passer le delta suivant » : le curseur d'émission a déjà
   * avancé, donc ces blocs ne seront **jamais** réécrits. Continuer produirait un
   * acte avec un **trou** au milieu, sans la moindre erreur affichée, que
   * l'auto-enregistrement de fin persisterait comme s'il était complet et que
   * l'export au dossier figerait. C'est exactement l'« abandon silencieux suivi
   * d'une reprise » que ce ticket interdit — et avant la mise en file, la même
   * exception traversait l'effet React jusqu'à la frontière d'erreur, donc elle
   * n'était jamais muette.
   */
  #surEchec(confirmes: number, totalDuDelta: number, erreur: unknown): ResultatEcriture {
    const cause = enErreur(erreur);
    // Le décompte distingue ce qui est acquis de ce qui ne l'est pas : avec le
    // découpage, « N blocs non posés » n'est plus le delta entier dès que la
    // première tranche a abouti.
    this.#condamner(
      `L'écriture dans le document a échoué (${confirmes} bloc(s) posés, `
      + `${totalDuDelta - confirmes} non posés) : ${cause.message}. `
      + 'Le texte déjà écrit est conservé ; rouvrez le document pour relancer la rédaction.',
    );
    return { statut: 'echec', erreur: cause };
  }

  #borner(ecriture: Promise<void>): Promise<void> {
    return new Promise<void>((resoudre, rejeter) => {
      const echeance = setTimeout(() => {
        this.#attentesBornees.delete(clore);
        rejeter(DEPASSEMENT);
      }, this.#borneMs);
      // `abandonner()` appelle ce clos : la borne est désarmée et l'attente se
      // dénoue tout de suite. `#executer` relira `#abandonnee` juste après et
      // ne touchera rien — plutôt que de laisser le maillon pendu au sort d'une
      // écriture que plus personne n'attend.
      const clore = () => {
        clearTimeout(echeance);
        this.#attentesBornees.delete(clore);
        resoudre();
      };
      this.#attentesBornees.add(clore);
      ecriture.then(
        () => {
          clearTimeout(echeance);
          this.#attentesBornees.delete(clore);
          resoudre();
        },
        (erreur: unknown) => {
          clearTimeout(echeance);
          this.#attentesBornees.delete(clore);
          rejeter(erreur);
        },
      );
    });
  }

  #condamner(message: string): void {
    if (this.#condamnee) return;
    this.#condamnee = true;
    // Une file abandonnée n'a plus d'hôte à prévenir : le composant est démonté,
    // et le montage suivant a une instance neuve.
    if (this.#abandonnee) return;
    this.#surCondamnation?.(message);
  }
}

function enErreur(valeur: unknown): Error {
  return valeur instanceof Error ? valeur : new Error(String(valeur));
}
